import { markRole, timeMarkAt, timeMarksIn, timeMarkSuffix, type MarkRole, type TimePrecision } from '@dispositio/core';
import { Mark, mergeAttributes } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { describeTimeMark } from './timeMarkText';

/**
 * Zeitmarke (PLAN.md §16): Eine Textstelle sagt, wann etwas spielt („am Morgen des 3. März 1823“, „drei Tage später“). Gespeichert als
 * `[Textstelle]{zeit 1823-03-03T07:00 stunde ca}`; im Editor dezent unterstrichen, der Tooltip nennt die Bedeutung. Steht im Schema vor
 * dem Bezug (refSpan): ein Bezug umschließt die Zeitmarken in ihm. Nur in Textdokumenten.
 */
export const TimeMark = Mark.create({
  name: 'timeMark',
  inclusive: false,
  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-time') ?? '',
        // der Tooltip nennt die Bedeutung („3 Tage danach“, „03.03.1823 07:00“)
        renderHTML: (attrs) => ({
          'data-time': attrs.value as string,
          title: describeTimeMark(String(attrs.value ?? ''), (attrs.precision as TimePrecision | null) ?? null, !!attrs.approx, (attrs.role as MarkRole | null) ?? null),
        }),
      },
      precision: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-precision'),
        renderHTML: (attrs) => (attrs.precision ? { 'data-precision': attrs.precision as string } : {}),
      },
      approx: { default: false, parseHTML: (el) => el.hasAttribute('data-approx'), renderHTML: (attrs) => (attrs.approx ? { 'data-approx': '' } : {}) },
      // Beginn, Ende oder Dauer der Szene (PLAN.md §17); null: von selbst
      role: { default: null, parseHTML: (el) => el.getAttribute('data-role'), renderHTML: (attrs) => (attrs.role ? { 'data-role': attrs.role as string } : {}) },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-time]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ class: 'rt-time' }, HTMLAttributes), 0];
  },

  markdownTokenizer: {
    name: 'timeMark',
    level: 'inline',
    start: (src: string) => timeMarksIn(src)[0]?.from ?? -1,
    tokenize: (src, _tokens, lexer) => {
      const t = timeMarkAt(src, 0);
      if (!t) return undefined;
      const inner = src.slice(t.textFrom, t.textTo);
      return { type: 'timeMark', raw: src.slice(0, t.to), text: inner, value: t.value, precision: t.precision, approx: t.approx, role: markRole(t), tokens: lexer.inlineTokens(inner) };
    },
  },
  parseMarkdown: (token, h) => {
    const t = token as { value?: string; precision?: TimePrecision | null; approx?: boolean; role?: MarkRole | null };
    return h.applyMark('timeMark', h.parseInline(token.tokens ?? []), { value: t.value ?? '', precision: t.precision ?? null, approx: t.approx ?? false, role: t.role ?? null });
  },
  renderMarkdown: (node, h) => {
    const a = (node.attrs ?? {}) as { value?: string; precision?: TimePrecision | null; approx?: boolean; role?: MarkRole | null };
    return `[${h.renderChildren(node)}]${timeMarkSuffix(a.value ?? '', a.precision ?? null, a.approx ?? false, a.role ?? null)}`;
  },
});

export interface TimeRange {
  from: number;
  to: number;
  value: string;
  precision: TimePrecision | null;
  approx: boolean;
  role: MarkRole | null;
}

/** Alle Zeitmarken im Dokument (zusammenhängende Stellen mit gleichem Wert). */
export function timeRanges(doc: PMNode): TimeRange[] {
  const out: TimeRange[] = [];
  let cur: TimeRange | null = null;
  doc.descendants((node, pos) => {
    if (!node.isInline) return true;
    const m = node.marks.find((x) => x.type.name === 'timeMark');
    const attrs = { value: String(m?.attrs.value ?? ''), precision: (m?.attrs.precision as TimePrecision | null) ?? null, approx: !!m?.attrs.approx, role: (m?.attrs.role as MarkRole | null) ?? null };
    if (m && cur && cur.to === pos && cur.value === attrs.value && cur.precision === attrs.precision && cur.approx === attrs.approx && cur.role === attrs.role) cur.to = pos + node.nodeSize;
    else if (m) out.push((cur = { from: pos, to: pos + node.nodeSize, ...attrs }));
    else cur = null;
    return false;
  });
  return out;
}

/** Zeitmarke, die die Stelle ganz oder teilweise abdeckt (die erste) */
export const timeOver = (doc: PMNode, from: number, to: number): TimeRange | undefined => timeRanges(doc).find((t) => t.from < Math.max(to, from + 1) && t.to > from);
